import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { isAdmin } from '../shared/index.js';

import { BrandMark } from '../components/brand.js';
import { Spinner } from '../components/ui.js';
import { useBoards } from '../hooks/use-directory.js';
import { useSession } from '../auth/session.js';

interface ShellValue {
  readonly openNavigation: () => void;
}

const ShellContext = createContext<ShellValue | null>(null);

export function useShell(): ShellValue {
  const value = useContext(ShellContext);

  if (value === null) {
    throw new Error('useShell must be used inside Shell.');
  }

  return value;
}

const RAIL_ITEM =
  'flex items-center gap-3 rounded-field px-3 py-2 text-[14px] font-semibold transition';

function railClass({ isActive }: { isActive: boolean }): string {
  return isActive
    ? `${RAIL_ITEM} bg-white/10 text-white shadow-[inset_3px_0_0_0_var(--color-gold)]`
    : `${RAIL_ITEM} text-white/75 hover:bg-white/5 hover:text-white`;
}

export function Shell({ children }: { readonly children: ReactNode }): ReactNode {
  const { user, principal } = useSession();
  const { boards, isLoading } = useBoards();
  const [navigationOpen, setNavigationOpen] = useState(false);

  const openNavigation = useCallback(() => {
    setNavigationOpen(true);
  }, []);

  const closeNavigation = useCallback(() => {
    setNavigationOpen(false);
  }, []);

  const value = useMemo<ShellValue>(() => ({ openNavigation }), [openNavigation]);

  useEffect(() => {
    if (!navigationOpen) return undefined;

    const onEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNavigationOpen(false);
    };

    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [navigationOpen]);

  if (user === null || principal === null) return null;

  return (
    <ShellContext.Provider value={value}>
      <div className="flex min-h-dvh">
        {navigationOpen ? (
          <button
            type="button"
            aria-label="Close the navigation"
            onClick={closeNavigation}
            className="fixed inset-0 z-20 bg-navy-deep/50 backdrop-blur-sm lg:hidden"
          />
        ) : null}

        <aside
          className={`fixed inset-y-0 left-0 z-30 flex w-rail max-w-[86vw] flex-col bg-rail shadow-rail transition-transform duration-200 ease-[var(--ease-entrance)] lg:translate-x-0 ${
            navigationOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="relative flex h-topbar shrink-0 items-center justify-center border-b border-white/10 px-6">
            <BrandMark tone="white" />
            <button
              type="button"
              aria-label="Close the navigation"
              onClick={closeNavigation}
              className="absolute top-1/2 right-4 -translate-y-1/2 rounded-field px-2 py-1 text-[20px] leading-none text-white/60 hover:text-white lg:hidden"
            >
              ×
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 pb-6 no-scrollbar">
            <p className="eyebrow mt-4 mb-2 px-3 text-white/35">Overview</p>
            <NavLink to="/" end className={railClass} onClick={closeNavigation}>
              Dashboard
            </NavLink>
            <NavLink to="/my-requests" className={railClass} onClick={closeNavigation}>
              My requests
            </NavLink>

            <p className="eyebrow mt-6 mb-2 px-3 text-white/35">Boards</p>
            {isLoading ? (
              <div className="px-3 py-2 text-white/50">
                <Spinner />
              </div>
            ) : boards.length === 0 ? (
              <p className="px-3 py-2 text-[13px] text-white/45">No board is open to you yet.</p>
            ) : (
              boards.map((entry) => (
                <NavLink
                  key={entry.board.id}
                  to={`/boards/${entry.board.slug}`}
                  className={railClass}
                  onClick={closeNavigation}
                >
                  <span className="size-2 shrink-0 rounded-full bg-cyan/70" />
                  <span className="truncate">{entry.board.name}</span>
                </NavLink>
              ))
            )}

            {isAdmin(principal) ? (
              <>
                <p className="eyebrow mt-6 mb-2 px-3 text-white/35">Administration</p>
                <NavLink to="/settings/boards" className={railClass} onClick={closeNavigation}>
                  Board setup
                </NavLink>
                <NavLink to="/settings/departments" className={railClass} onClick={closeNavigation}>
                  Departments
                </NavLink>
                <NavLink to="/settings/positions" className={railClass} onClick={closeNavigation}>
                  Positions
                </NavLink>
                <NavLink to="/settings/users" className={railClass} onClick={closeNavigation}>
                  People
                </NavLink>
              </>
            ) : null}
          </nav>
        </aside>

        <main className="flex min-h-dvh w-full min-w-0 flex-1 flex-col lg:ml-rail">{children}</main>
      </div>
    </ShellContext.Provider>
  );
}
